import { ThemeProvider as NovaThemeProvider } from '@fme-nova/components'
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import React from 'react'

import { createComponentWrapperWithRef } from './ComponentLibraryWrapper'
import { standardTransitionDuration } from './costants'
import { MUI_PALETTE } from './palette'

const WHITE_SPACE_STYLE = { whiteSpace: 'pre' }

const THEME_OPTIONS: ThemeOptions = {
  components: {
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: '#B31B32',
          '&error': {
            color: '#B31B32',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: MUI_PALETTE.white.main,
          padding: '8px 16px',
          backgroundColor: MUI_PALETTE.components.tooltip.fill,
          fontFamily: 'Arial',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '143%',
          maxWidth: '320px',
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: '700',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '.MuiBox-root': WHITE_SPACE_STYLE,
        },
        option: WHITE_SPACE_STYLE,
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: WHITE_SPACE_STYLE,
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: WHITE_SPACE_STYLE,
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: WHITE_SPACE_STYLE,
        asterisk: {
          color: '#B31B32',
          '&error': {
            color: '#B31B32',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { whiteSpace: 'pre-wrap' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          /* EDGE */
          '::-ms-reveal, ::-ms-clear': {
            display: 'none',
          },
          /* SAFARI */
          '::-webkit-contacts-auto-fill-button, ::-webkit-credentials-auto-fill-button': {
            visibility: 'hidden',
          },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
  },
  transitions: {
    duration: { standard: standardTransitionDuration },
  },
  typography: {
    fontFamily: [
      'Arial',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),

    h1: {
      fontFamily: 'Arial',
      fontSize: '3rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '116.7%',
    },

    h2: {
      fontFamily: 'Arial',
      fontSize: '2.25rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '111.1%',
    },

    h3: {
      fontFamily: 'Arial',
      fontSize: '1.75rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '114.2%',
    },

    h4: {
      fontFamily: 'Arial',
      fontSize: '1.5rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '133.3%',
    },

    h5: {
      fontFamily: 'Arial',
      fontSize: '1.25rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '120%',
    },

    h6: {
      fontFamily: 'Arial',
      fontSize: '1.125rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '133.3%',
    },

    subtitle1: {
      fontFamily: 'Arial',
      fontSize: '1rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
    },

    subtitle2: {
      fontFamily: 'Arial',
      fontSize: '0.875rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '143%',
    },

    body1: {
      fontFamily: 'Arial',
      fontSize: '1rem',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '150%',
    },

    body2: {
      fontFamily: 'Arial',
      fontSize: '0.875rem',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
    },

    button: {
      fontFamily: 'Arial',
      fontSize: '0.938rem',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '143%',
      textTransform: 'unset',
    },

    caption: {
      fontFamily: 'Arial',
      fontSize: '0.75rem',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '166%',
    },

    overline: {
      fontFamily: 'Arial',
      fontSize: '0.75rem',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '266%',
      textTransform: 'uppercase',
    },
  },
}

const ID_TAGGING_THEME_OPTIONS: ThemeOptions = {
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          maxWidth: 'unset',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          overflow: 'visible !important',
        },
      },
    },
  },
}

type ThemeProviderProps = {
  children: React.ReactNode
  idTaggingEnabled?: boolean
}

const MuiThemeProviderWrapper = ({ children, idTaggingEnabled }: ThemeProviderProps) => {
  const BASE_THEME = createTheme(THEME_OPTIONS, idTaggingEnabled ? ID_TAGGING_THEME_OPTIONS : {})

  return <MuiThemeProvider theme={BASE_THEME}>{children}</MuiThemeProvider>
}

export const ThemeProvider = createComponentWrapperWithRef<ThemeProviderProps>(
  {
    mui: MuiThemeProviderWrapper,
    nova: NovaThemeProvider,
  },
  'ThemeProvider',
)
